import {
  Provider,
  SiteVariablesPrepared,
  ThemePrepared,
  teamsDarkTheme,
  teamsHighContrastTheme,
  teamsTheme,
} from "@fluentui/react-northstar";
import { AC, TeamsDark, TeamsHighContrast, TeamsLight } from "@teams/cards";
import { TeamsApplicationTypes } from "@teams/types";
import React, { createContext, useContext, useEffect, useState } from "react";

import { PageContext } from "../utils/teamsHelpers";

type ThemeState = {
  siteVariables: SiteVariablesPrepared;
  themeName: string;
  hostConfig: AC.HostConfig;
};

type ComponentProps = {
  themeName: string;
  pageContext: PageContext;
};

const DEFAULT_THEME_STATE = {
  context: undefined,
  themeName: "default",
  siteVariables: teamsTheme.siteVariables,
  theme: teamsTheme,
  hostConfig: TeamsLight,
};

const ThemeContext = createContext<ThemeState>(DEFAULT_THEME_STATE);

export const ThemeProvider: React.FC<ComponentProps> = ({ themeName, pageContext, children }) => {
  const [theme, setTheme] = useState<ThemePrepared<any>>(teamsTheme);
  const [appSiteVariables, setAppSiteVariables] = useState<
    Partial<SiteVariablesPrepared> | undefined
  >(undefined);
  const [mergedSiteVariables, setMergedSiteVariables] = useState<SiteVariablesPrepared>(
    theme.siteVariables
  );

  useEffect(() => {
    // export theme values into CSS vars so they could be referenced from CSS
    function setCssVar(prefix: string, key: string, value: number | string | Record<string, any>) {
      const fullKey = prefix.length === 0 ? key : `${prefix}-${key}`;
      if (typeof value === "number" || typeof value === "string") {
        document.documentElement.style.setProperty(fullKey, value.toString());
      } else {
        Object.keys(value).forEach((prop: string) => {
          if (value[prop]) {
            setCssVar(fullKey, prop, value[prop]);
          }
        });
      }
    }

    let brandColor: string;
    let mergedSiteVariables = getTheme(themeName).siteVariables;
    switch (pageContext) {
      case "/agile":
        brandColor = "#4d5098";
        break;
      case "/msteams":
        brandColor = "#0190F7";
        const appSiteVariables = getApplicationTheme("msteams", themeName);
        setAppSiteVariables(appSiteVariables);
        mergedSiteVariables = mergeBrandSiteVariables(mergedSiteVariables, appSiteVariables);
        break;
    }

    if (themeName === "dark" || themeName === "contrast") {
      setCssVar("", "--cards-accent-color", "#a6a7dc");
      setCssVar("", "--preview-question-container-color", "#292828");
    } else {
      setCssVar("", "--cards-accent-color", "#5b5fc7");
      setCssVar("", "--preview-question-container-color", "#f9f8f7");
    }

    setCssVar("", "--polly-accent-color", brandColor);

    setCssVar("", "--polly-hot-take-up-vote-stroke", "110, 231, 183");
    setCssVar("", "--polly-hot-take-up-vote-bg", "142, 209, 178");
    setCssVar("", "--polly-hot-take-down-vote-stroke", "253, 164, 175");
    setCssVar("", "--polly-hot-take-down-vote-bg", "253, 188, 180");

    setCssVar("", "--stardustui", mergedSiteVariables);

    setMergedSiteVariables(mergedSiteVariables);
    setTheme(getTheme(themeName));
  }, [themeName, pageContext]);

  const appTheme = { siteVariables: appSiteVariables };

  return (
    <Provider theme={theme} style={{ backgroundColor: "transparent", height: "100%" }}>
      <Provider theme={appTheme} style={{ backgroundColor: "transparent", height: "100%" }}>
        <ThemeContext.Provider
          value={{
            themeName,
            siteVariables: mergedSiteVariables,
            hostConfig: getHostConfig(themeName),
          }}
        >
          {children}
        </ThemeContext.Provider>
      </Provider>
    </Provider>
  );
};
export const useTheme = () => useContext(ThemeContext);

const getHostConfig = (themeName: string): AC.HostConfig => {
  switch (themeName) {
    case "dark":
      return TeamsDark;
    case "contrast":
      return TeamsHighContrast;
  }
  return TeamsLight;
};

const getTheme = (themeVar: string): ThemePrepared => {
  switch (themeVar) {
    case "dark":
      return teamsDarkTheme;
    case "contrast":
      return teamsHighContrastTheme;
    case "default":
    default:
      return teamsTheme;
  }
};

const getApplicationTheme = (applicationgType: TeamsApplicationTypes, themeType: string) => {
  // * don't change the theme for high contrast
  // * only Core sings the Polly blues
  if (themeType === "contrast" || applicationgType !== "msteams") {
    return undefined;
  }

  const brand = {
    "50": "#F4F9FC",
    "100": "#E1F5FD",
    "200": "#CFEBFE",
    "300": "#81C5FC",
    "400": "#81C5FC",
    "500": "#0190F7",
    "600": "#0190F7",
    "700": "#0190F7",
    "800": "#27638E",
    "900": "#1E4963",
    "1000": "#07133D",
  };

  // scheme pulled from the Teams theme
  // https://github.com/microsoft/fluentui/blob/18799a4d2f/packages/fluentui/react-northstar/src/themes/teams/colors.ts#L213
  const colorScheme = {
    brand: {
      foreground: brand[600],
      foreground1: brand[600],
      foreground2: brand[700],
      foreground3: brand[200],

      background: brand[700], // buttons
      background1: brand[100],
      background2: brand[900],
      background3: brand[1000],
      background4: brand[800],

      border: "#E1DFDD",
      border1: brand[200],
      border2: brand[300],

      foregroundHover: brand[600],
      foregroundHover2: brand[200],

      borderHover: brand[300],

      backgroundHover: brand[800], // buttons
      backgroundHover1: brand[50],
      backgroundHover2: brand[100],

      foregroundPressed: brand[800],
      backgroundPressed: brand[800],
      borderPressed: brand[300],

      foregroundActive: brand[600],
      foregroundActive1: brand[600],
      foregroundActive2: brand[200],

      backgroundActive: brand[600],
      backgroundActive1: brand[700], // toggles

      borderActive1: brand[200],
      borderActive2: brand[300],

      foregroundFocus: brand[600],
      foregroundFocus1: brand[600],
      foregroundFocus2: brand[700],
      foregroundFocus3: brand[200],

      backgroundFocus: brand[600],
      backgroundFocus1: brand[100],
      backgroundFocus2: brand[900],
      backgroundFocus3: brand[1000],

      borderFocus1: brand[600],
    },
  };

  // site variables to be overridden
  const siteVariables = {
    colors: {
      brand,
    },
    contextualColors: {
      brand,
    },
    colorScheme,
  };

  return siteVariables;
};

const mergeBrandSiteVariables = (
  themeSiteVariables: SiteVariablesPrepared,
  brandSiteVariables: any
) => {
  return {
    ...themeSiteVariables,
    colors: {
      ...themeSiteVariables.colors,
      brand: brandSiteVariables?.colors.brand,
    },
    contextualColors: {
      ...themeSiteVariables.contextualColors,
      brand: brandSiteVariables?.contextualColors.brand,
    },
    colorScheme: {
      ...themeSiteVariables.colorScheme,
      brand: brandSiteVariables?.colorScheme.brand,
      default: {
        ...themeSiteVariables.colorScheme.default,
        ...brandSiteVariables?.colorScheme.default,
      },
    },
  };
};
